import SharedWallClock from "../../amplify/backend/function/matesonlinebookingMobLibrary/opt/WallClock.js";

/**
 * The storefront's adapter onto the one writer and the readers of wall-clock time (MOB-058).
 *
 * The rules live in the Lambda layer, the same way as `PointSellLock` — see ADR 0001. Storefront
 * call sites import this module, not the layer file, so a storefront-only concern has one place
 * to go. Do not copy the layer file back here. A helper that the Lambdas need too goes into the
 * layer module, and this file only names it.
 */
export default SharedWallClock;

/** `WallClock.text` of the layer: the digits in `format`, or `Invalid date`. */
export function wallClockText(value, format) {
  return SharedWallClock.text(value, format);
}

/**
 * The one form of `WallClock.toWire`, with the seconds at 0. The rental availability queries ask
 * for whole minutes (MOB-058). `null` and `undefined` give `null`, and an unreadable value throws,
 * as in `toWire`.
 */
export function toWireMinute(value) {
  const wire = SharedWallClock.toWire(value);
  // `YYYY-MM-DDTHH:mm:` is 17 characters long.
  return wire == null ? null : wire.slice(0, 17) + "00Z";
}

/**
 * The value of `a` and `b` with the earlier digits, unchanged, so that a caller can pass a stored
 * value on as it came from the API. The offsets do not count. A value with no readable digits
 * loses to a readable one, as in `PointSellLock.orderrPointSells`. A tie gives `a`.
 */
export function earlierWallClock(a, b) {
  const keyA = SharedWallClock.sortKey(a);
  const keyB = SharedWallClock.sortKey(b);
  if (keyA == null) return keyB == null ? a : b;
  if (keyB == null) return a;
  return keyB < keyA ? b : a;
}

/** As `earlierWallClock`, but the value with the later digits. */
export function laterWallClock(a, b) {
  const keyA = SharedWallClock.sortKey(a);
  const keyB = SharedWallClock.sortKey(b);
  if (keyA == null) return keyB == null ? a : b;
  if (keyB == null) return a;
  return keyB > keyA ? b : a;
}

/**
 * The whole minutes from the digits of `from` to the digits of `to`, as `diff(..., "minutes")`
 * of moment counts them. The offsets do not count, so 08:00 `Z` to 10:00 `+02:00` is 120.
 * `NaN` when a value has no readable digits.
 */
export function wallClockMinutes(from, to) {
  const keyFrom = SharedWallClock.sortKey(from);
  const keyTo = SharedWallClock.sortKey(to);
  if (keyFrom == null || keyTo == null) return NaN;
  return Math.trunc((keyTo - keyFrom) / 60000);
}

/** `WallClock.compare` of the layer: a sort by the digits, with an unreadable value last. */
export function compareWallClock(a, b) {
  return SharedWallClock.compare(a, b);
}
